import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, FileText, History, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { useAuth } from "@/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ListFilterBar from "@/components/ListFilterBar";
import DocumentNumberLink from "@/components/DocumentNumberLink";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import {
  isWithinDateRange,
  useClearFilterParams,
  useFilterParam,
} from "@/hooks/useFilterParam";

interface WaybillRow {
  id: number;
  waybill_number: string;
  has_sales_invoice?: boolean;
  quote_id: number | null;
  quotation_ref: string;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  waybill_date: string | null;
  driver_name: string;
  vehicle_number: string;
}

interface WaybillCancellation {
  id: number;
  waybill_number: string;
  quotation_ref: string;
  customer_name: string;
  waybill_date: string | null;
  driver_name: string;
  vehicle_number: string;
  cancellation_reason: string;
  cancelled_by: string;
  cancelled_at: string | null;
}

export default function Waybills() {
  const nav = useNavigate();
  const { can } = useAuth();
  const canEdit = can("waybills.write");
  const canCancel = can("waybills.cancel");
  const canRead = can("waybills.read");

  const [rows, setRows] = useState<WaybillRow[]>([]);
  const [cancellations, setCancellations] = useState<WaybillCancellation[]>([]);
  const [search, setSearch] = useFilterParam("q");
  const [docNo, setDocNo] = useFilterParam("doc_no");
  const [customerQuery, setCustomerQuery] = useFilterParam("customer");
  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const [tab, setTab] = useState<"active" | "cancelled">("active");
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const clearFilters = useClearFilterParams([
    "q",
    "doc_no",
    "customer",
    "date_from",
    "date_to",
  ]);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<WaybillRow | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Distinct customer names from both active + cancelled lists so the
  // datalist stays useful regardless of which tab is currently open.
  const customerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) if (r.customer_name?.trim()) names.add(r.customer_name);
    for (const c of cancellations) {
      if (c.customer_name?.trim()) names.add(c.customer_name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows, cancellations]);

  const hasAnyFilter = Boolean(
    search || docNo || customerQuery || dateFrom || dateTo,
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const docNeedle = docNo.trim().toLowerCase();
    const custNeedle = customerQuery.trim().toLowerCase();
    return rows.filter((r) => {
      const number = r.waybill_number.toLowerCase();
      const cust = r.customer_name.toLowerCase();
      const ref = (r.quotation_ref || "").toLowerCase();
      const searchMatch =
        !s ||
        number.includes(s) ||
        cust.includes(s) ||
        ref.includes(s);
      const docMatch = !docNeedle || number.includes(docNeedle);
      const custMatch = !custNeedle || cust.includes(custNeedle);
      const dateMatch = isWithinDateRange(r.waybill_date, dateFrom, dateTo);
      return searchMatch && docMatch && custMatch && dateMatch;
    });
  }, [rows, search, docNo, customerQuery, dateFrom, dateTo]);

  const filteredCancelled = useMemo(() => {
    const s = search.trim().toLowerCase();
    const docNeedle = docNo.trim().toLowerCase();
    const custNeedle = customerQuery.trim().toLowerCase();
    return cancellations.filter((c) => {
      const number = c.waybill_number.toLowerCase();
      const cust = c.customer_name.toLowerCase();
      const ref = (c.quotation_ref || "").toLowerCase();
      const searchMatch =
        !s ||
        number.includes(s) ||
        cust.includes(s) ||
        ref.includes(s);
      const docMatch = !docNeedle || number.includes(docNeedle);
      const custMatch = !custNeedle || cust.includes(custNeedle);
      const dateMatch = isWithinDateRange(c.waybill_date, dateFrom, dateTo);
      return searchMatch && docMatch && custMatch && dateMatch;
    });
  }, [cancellations, search, docNo, customerQuery, dateFrom, dateTo]);

  const activePg = usePagination(filtered);
  const cancelledPg = usePagination(filteredCancelled);

  useEffect(() => {
    activePg.setPage(1);
    cancelledPg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, docNo, customerQuery, dateFrom, dateTo]);

  async function loadActive() {
    setLoading(true);
    try {
      const res = await api.get<{ waybills: WaybillRow[] }>("/waybills");
      setRows(res.waybills ?? []);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load waybills");
    } finally {
      setLoading(false);
    }
  }

  async function loadCancellations() {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await api.get<{ cancellations: WaybillCancellation[] }>(
        "/waybills/cancellations/history",
      );
      setCancellations(res.cancellations ?? []);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Failed to load cancellations";
      setHistoryError(msg);
      setCancellations([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    loadActive();
    loadCancellations();
  }, []);

  function openCancelDialog(r: WaybillRow) {
    setCancelTarget(r);
    setCancelReason("");
    setCancelOpen(true);
  }

  async function confirmCancel() {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      toast.error("Please enter a reason for cancellation");
      return;
    }
    setCancelling(true);
    try {
      await api.del(`/waybills/${cancelTarget.id}`, {
        reason: cancelReason.trim(),
      });
      toast.success("Waybill cancelled");
      setCancelOpen(false);
      setCancelTarget(null);
      setCancelReason("");
      setTab("cancelled");
      await Promise.all([loadActive(), loadCancellations()]);
    } catch (err) {
      toast.error((err as Error).message || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Waybills</h1>
          <p className="text-muted-foreground">
            Delivery waybills for dispatched goods.
          </p>
        </div>
        {canEdit ? (
          <Button
            onClick={() => nav("/waybills/new")}
            className="bg-violet-500 hover:bg-violet-400 text-white rounded-xl px-5"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Waybill
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <ListFilterBar
          title="Search Waybill"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search waybills..."
          docNo={{
            value: docNo,
            onChange: setDocNo,
            placeholder: "Waybill No.",
          }}
          customer={{
            value: customerQuery,
            onChange: setCustomerQuery,
            options: customerOptions,
            placeholder: "Customer",
          }}
          dateFrom={{ value: dateFrom, onChange: setDateFrom }}
          dateTo={{ value: dateTo, onChange: setDateTo }}
          hasAnyFilter={hasAnyFilter}
          onClear={clearFilters}
        />
        <Card className="shadow-card">
          <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as "active" | "cancelled")}
          className="w-full"
        >
          <div className="px-4 pt-3 border-b">
            <TabsList>
              <TabsTrigger value="active">Active ({rows.length})</TabsTrigger>
              <TabsTrigger value="cancelled" className="gap-1.5">
                <History className="h-3.5 w-3.5" />
                Cancelled ({cancellations.length})
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="active" className="mt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Waybill No.</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Quote Ref</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground py-10"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : null}
                {!loading && filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground py-10"
                    >
                      No waybills yet.
                    </TableCell>
                  </TableRow>
                ) : null}
                {!loading &&
                  activePg.pageItems.map((r, i) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-muted-foreground">
                        {activePg.from + i}
                      </TableCell>
                      <TableCell className="font-mono">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <DocumentNumberLink
                            to={`/waybills/${r.id}`}
                            canOpen={canRead}
                          >
                            {r.waybill_number}
                          </DocumentNumberLink>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {r.customer_name || "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.quotation_ref || "—"}
                      </TableCell>
                      <TableCell>
                        {r.waybill_date
                          ? new Date(r.waybill_date).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>{r.driver_name || "—"}</TableCell>
                      <TableCell>{r.vehicle_number || "—"}</TableCell>
                      <TableCell className="text-center">
                        <div className="inline-flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() =>
                                    nav(`/waybills/${r.id}?openPdf=1`)
                                  }
                                  disabled={!r.has_sales_invoice}
                                  title={
                                    r.has_sales_invoice
                                      ? "Preview PDF"
                                      : "Generate a Sales Invoice for this waybill before previewing/printing."
                                  }
                                >
                                  <FileText className="h-4 w-4 text-primary" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!r.has_sales_invoice ? (
                              <TooltipContent>
                                Generate a Sales Invoice for this waybill before
                                previewing/printing.
                              </TooltipContent>
                            ) : null}
                          </Tooltip>
                          {canEdit && !r.has_sales_invoice ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => nav(`/waybills/${r.id}/edit`)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4 text-primary" />
                            </Button>
                          ) : null}
                          {canCancel && !r.has_sales_invoice ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => openCancelDialog(r)}
                              title="Cancel waybill"
                            >
                              <Ban className="h-4 w-4 text-destructive" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {!loading && filtered.length > 0 ? (
              <Pagination
                page={activePg.page}
                pageCount={activePg.pageCount}
                total={activePg.total}
                from={activePg.from}
                to={activePg.to}
                onPrev={activePg.prev}
                onNext={activePg.next}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="cancelled" className="mt-0">
            {historyError ? (
              <div className="px-4 py-6 text-center text-sm text-destructive">
                {historyError}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 ml-3"
                  onClick={loadCancellations}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Waybill No.</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Quote Ref</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Cancelled By</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground py-10"
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {!historyLoading && filteredCancelled.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground py-10"
                      >
                        No cancelled waybills yet.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {!historyLoading &&
                    cancelledPg.pageItems.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono">
                          <DocumentNumberLink
                            to={`/waybills/cancelled/${c.id}`}
                            canOpen={canRead}
                          >
                            {c.waybill_number}
                          </DocumentNumberLink>
                        </TableCell>
                        <TableCell>{c.customer_name}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {c.quotation_ref || "—"}
                        </TableCell>
                        <TableCell>
                          {c.waybill_date
                            ? new Date(c.waybill_date).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell>{c.cancelled_by}</TableCell>
                        <TableCell className="max-w-[240px] text-sm whitespace-pre-wrap">
                          {c.cancellation_reason}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() =>
                              nav(`/waybills/cancelled/${c.id}?openPdf=1`)
                            }
                            title="Preview cancelled waybill PDF"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            {!historyError &&
            !historyLoading &&
            filteredCancelled.length > 0 ? (
              <Pagination
                page={cancelledPg.page}
                pageCount={cancelledPg.pageCount}
                total={cancelledPg.total}
                from={cancelledPg.from}
                to={cancelledPg.to}
                onPrev={cancelledPg.prev}
                onNext={cancelledPg.next}
              />
            ) : null}
          </TabsContent>
          </Tabs>
        </Card>
      </div>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Waybill</DialogTitle>
          </DialogHeader>
          {cancelTarget ? (
            <p className="text-sm text-muted-foreground">
              Cancelling{" "}
              <span className="font-mono text-foreground">
                {cancelTarget.waybill_number}
              </span>
              {cancelTarget.customer_name
                ? ` — ${cancelTarget.customer_name}`
                : ""}
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label>Reason for cancellation *</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              placeholder="Enter why this waybill is being cancelled..."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
            >
              Close
            </Button>
            <Button
              onClick={confirmCancel}
              disabled={cancelling}
              variant="destructive"
            >
              {cancelling ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
